// Shared by CRA and Express. Builds a factual "About this part" paragraph from
// structured product data (part numbers, fitment, specs), so near-identical
// products get distinct, searchable text. It never invents facts: every
// sentence is skipped when its data is missing.

const MAX_VEHICLES = 4;
const MAX_NUMBERS = 6;

const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
// Only fix all-lowercase input, so names like "ILX" or "CR-V" keep their casing.
const titleCase = (value) => {
  const text = clean(value);
  return text === text.toLowerCase() ? text.replace(/\b[a-z]/g, (c) => c.toUpperCase()) : text;
};
const uniq = (values) => [...new Set(values.map(clean).filter(Boolean))];
const isEngineOil = (p) => /^engine[- ]oil$/i.test(clean(p.category));

const listText = (items, max) => {
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`;
  if (shown.length <= 1) return shown.join('');
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
};

const yearText = (years) => {
  const nums = [...new Set(years.map(Number).filter((y) => Number.isInteger(y) && y > 1900))].sort((a, b) => a - b);
  if (!nums.length) return '';
  return nums[0] === nums[nums.length - 1] ? String(nums[0]) : `${nums[0]}–${nums[nums.length - 1]}`;
};

// "2016–2021 Honda Civic", grouped by make/model, most common first.
export const fitmentVehicles = (fitments = []) => {
  const groups = new Map();
  for (const f of Array.isArray(fitments) ? fitments : []) {
    const make = titleCase(f?.make);
    const model = titleCase(f?.model);
    if (!make || !model) continue;
    const key = `${make} ${model}`;
    const group = groups.get(key) || { name: key, years: [], count: 0 };
    if (f.year != null) group.years.push(f.year);
    group.count += 1;
    groups.set(key, group);
  }
  return [...groups.values()]
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((g) => clean(`${yearText(g.years)} ${g.name}`));
};

const partType = (p) => {
  const category = clean(p.category);
  if (/brake\s*(disc|rotor)/i.test(category)) return 'brake rotor';
  if (/brake\s*pad/i.test(category)) return 'brake pad set';
  return category.toLowerCase().replace(/s$/, '') || 'part';
};

const positionText = (p) => {
  const position = clean(p.position);
  return position && position !== 'none' ? `${position.replace(/-/g, ' ')} ` : '';
};

const yes = (value) => clean(value) === 'Yes';

const specSentences = (p) => {
  const facts = [];
  const rotor = p.brakeRotorSpecs || {};
  const pad = p.brakePadSpecs || {};
  if (rotor.rotorConstruction) facts.push(`${rotor.rotorConstruction.toLowerCase()} design`);
  if (Number(rotor.outerDiameterMm) > 0) facts.push(`${rotor.outerDiameterMm} mm outer diameter`);
  if (Number(rotor.nominalThicknessMm) > 0) facts.push(`${rotor.nominalThicknessMm} mm nominal thickness`);
  if (Number(rotor.minimumThicknessMm) > 0) facts.push(`${rotor.minimumThicknessMm} mm minimum thickness`);
  if (Number(rotor.studLugHoleQuantity) > 0) facts.push(`${rotor.studLugHoleQuantity} lug holes`);
  const coating = rotor.coatingType === 'Other' ? clean(rotor.coatingTypeOther) : clean(rotor.coatingType);
  if (coating) facts.push(`${coating.toLowerCase()} finish`);
  else if (yes(rotor.rustResistantCoated)) facts.push('rust-resistant coating');
  if (clean(p.material)) facts.push(`${clean(p.material).toLowerCase()} material`);
  if (Number(pad.assemblyThicknessMm) > 0) facts.push(`${pad.assemblyThicknessMm} mm assembly thickness`);
  const included = [
    yes(pad.padWearSensorIncluded) && 'wear sensor',
    yes(pad.mountingHardwareIncluded) && 'mounting hardware',
    yes(pad.brakeLubricantIncluded) && 'brake lubricant',
  ].filter(Boolean);
  const drivetrain = clean(rotor.drivetrainType || pad.drivetrainType);
  return [
    facts.length ? `Specifications: ${listText(facts, 8)}.` : '',
    included.length ? `Includes ${listText(included, 3)}.` : '',
    drivetrain && drivetrain !== 'Fits All Drivetrains' ? `Designed for ${drivetrain} vehicles.` : '',
  ].filter(Boolean);
};

const engineOilSentences = (p) => {
  const attrs = p.attributes || {};
  const grade = clean(attrs.Viscosity);
  const volume = clean(attrs.Volume);
  const specs = clean(attrs['OEM / Manufacturer Specifications'] || attrs.Specifications);
  const sentences = [];
  if (grade || volume) sentences.push(`This is ${grade ? `${grade} ` : ''}engine oil${volume ? ` in a ${volume} container` : ''}${p.brand ? ` from ${clean(p.brand)}` : ''}.`);
  if (specs) sentences.push(`It is listed for ${specs}.`);
  sentences.push('Always confirm the viscosity and approval required in your owner’s manual.');
  return sentences;
};

export const buildProductSummary = (p = {}) => {
  if (isEngineOil(p)) {
    const sentences = engineOilSentences(p);
    return sentences.length > 1 ? sentences.join(' ') : '';
  }

  const sentences = [];
  const brand = clean(p.brand);
  const partNumber = clean(p.partNumber);
  sentences.push(`This ${positionText(p)}${partType(p)}${brand ? ` from ${brand}` : ''}${partNumber ? ` has part number ${partNumber}` : ''}${p.sku && clean(p.sku) !== partNumber ? ` (KCC SKU ${clean(p.sku)})` : ''}.`);

  const vehicles = fitmentVehicles(p.fitments);
  if (vehicles.length) sentences.push(`It fits ${listText(vehicles, MAX_VEHICLES)}.`);

  const oem = uniq(p.oemNumbers || []);
  if (oem.length) sentences.push(`It replaces OEM part number${oem.length > 1 ? 's' : ''} ${listText(oem, MAX_NUMBERS)}.`);

  const interchange = uniq((p.interchangeables || []).map((i) => `${clean(i?.brandLabel)} ${clean(i?.partNumber)}`));
  if (interchange.length) sentences.push(`Cross-references: ${listText(interchange, MAX_NUMBERS)}.`);

  sentences.push(...specSentences(p));

  // The opening sentence alone just restates the title; only show real detail.
  return sentences.length > 1 ? sentences.join(' ') : '';
};
